import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Localized } from './types';

export type Lang = 'en' | 'te';

/** UI strings. Content strings come from the database as { en, te }. */
const STRINGS = {
  search: { en: 'Search', te: 'వెతకండి' },
  searchPlaceholder: { en: 'Search temples, businesses, places, villages…', te: 'దేవాలయాలు, వ్యాపారాలు, ప్రదేశాలు వెతకండి…' },
  submitListing: { en: 'Submit Listing', te: 'లిస్టింగ్ సమర్పించండి' },
  viewAll: { en: 'View all', te: 'అన్నీ చూడండి' },
  verified: { en: 'Verified', te: 'ధృవీకరించబడింది' },
  call: { en: 'Call', te: 'కాల్' },
  whatsapp: { en: 'WhatsApp', te: 'వాట్సాప్' },
  directions: { en: 'Directions', te: 'దారి' },
  share: { en: 'Share', te: 'షేర్' },
  report: { en: 'Report', te: 'నివేదించండి' },
  suggestEdit: { en: 'Suggest an edit', te: 'సవరణ సూచించండి' },
  sample: { en: 'Sample content', te: 'నమూనా కంటెంట్' },
  openingHours: { en: 'Opening hours', te: 'పని వేళలు' },
  closed: { en: 'Closed', te: 'మూసివేయబడింది' },
  address: { en: 'Address', te: 'చిరునామా' },
  contact: { en: 'Contact', te: 'సంప్రదించండి' },
  gallery: { en: 'Gallery', te: 'గ్యాలరీ' },
  related: { en: 'Related', te: 'సంబంధిత' },
  nearby: { en: 'Nearby', te: 'సమీపంలో' },
  history: { en: 'History', te: 'చరిత్ర' },
  festivals: { en: 'Festivals', te: 'పండుగలు' },
  upcoming: { en: 'Upcoming', te: 'రాబోయేవి' },
  ongoing: { en: 'Happening now', te: 'జరుగుతున్నవి' },
  past: { en: 'Past', te: 'గతం' },
  allCategories: { en: 'All categories', te: 'అన్ని వర్గాలు' },
  allVillages: { en: 'All villages', te: 'అన్ని గ్రామాలు' },
  noResults: { en: 'Nothing found', te: 'ఏమీ కనబడలేదు' },
  loadError: { en: 'We could not load this content.', te: 'కంటెంట్ లోడ్ కాలేదు.' },
  retry: { en: 'Try again', te: 'మళ్లీ ప్రయత్నించండి' },
  home: { en: 'Home', te: 'హోమ్' },
  map: { en: 'Map', te: 'మ్యాప్' },
  grid: { en: 'Grid', te: 'గ్రిడ్' },
  contribute: { en: 'Contribute', te: 'సహకరించండి' },
  previous: { en: 'Previous', te: 'మునుపటి' },
  next: { en: 'Next', te: 'తదుపరి' },
  menu: { en: 'Menu', te: 'మెనూ' },
  close: { en: 'Close', te: 'మూసివేయి' },
  language: { en: 'తెలుగు', te: 'English' },
  deity: { en: 'Deity', te: 'దేవత' },
  darshan: { en: 'Darshan hours', te: 'దర్శన వేళలు' },
  services: { en: 'Services & products', te: 'సేవలు & ఉత్పత్తులు' },
  website: { en: 'Website', te: 'వెబ్‌సైట్' },
  email: { en: 'Email', te: 'ఈమెయిల్' },
  organizer: { en: 'Organiser', te: 'నిర్వాహకులు' },
  venue: { en: 'Venue', te: 'వేదిక' },
  photoBy: { en: 'Photo', te: 'ఫోటో' },
  sources: { en: 'Sources', te: 'మూలాలు' },
  temporarilyClosed: { en: 'Temporarily closed', te: 'తాత్కాలికంగా మూసివేయబడింది' },
} satisfies Record<string, Localized>;

export type StringKey = keyof typeof STRINGS;

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: StringKey) => string;
  /** Picks the current language from a { en, te } value, falling back to the other language. */
  L: (v?: Partial<Localized> | null) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem('ylm-lang');
    if (saved === 'en' || saved === 'te') return saved;
  } catch {
    /* storage unavailable */
  }
  return 'en';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.classList.toggle('lang-te', lang === 'te');
  }, [lang]);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem('ylm-lang', l);
    } catch {
      /* ignore */
    }
  }, []);
  const value = useMemo<I18nCtx>(
    () => ({
      lang,
      setLang,
      t: (key) => STRINGS[key][lang],
      L: (v) => (v ? (lang === 'te' ? v.te || v.en : v.en || v.te) || '' : ''),
    }),
    [lang, setLang],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useI18n outside provider');
  return c;
}
